// app/javascript/widget/lightbox.ts
//
// Лайтбокс галереи: полноэкранный просмотр карточек (фото, подпись, цена),
// листание кнопками, стрелками и свайпом, закрытие по Escape и клику мимо,
// возврат фокуса тому, кто открыл. Живёт отдельно от ChatWidget: свой DOM,
// своё состояние, тестируется в jsdom (lightbox.test.mjs).

import type { GalleryCard } from './api';
import { splitPrice } from './gallery.ts';

/** Минимальный сдвиг пальца, чтобы свайп считался листанием. */
export const SWIPE_MIN_PX = 40;

/** Что лайтбоксу нужно от виджета: фабрика узлов, переводы и корень для монтирования. */
export interface LightboxHost {
  el<K extends keyof HTMLElementTagNameMap>(
    tag: K,
    className: string,
    text?: string,
  ): HTMLElementTagNameMap[K];
  t(key: string): string;
  root: HTMLElement;
  /** Кто сейчас в фокусе внутри виджета — ему вернём фокус после закрытия. */
  activeElement(): HTMLElement | null;
}

interface Open {
  box: HTMLDivElement;
  image: HTMLImageElement;
  caption: HTMLDivElement;
  counter: HTMLDivElement;
  cards: GalleryCard[];
  index: number;
  opener: HTMLElement | null;
}

export class Lightbox {
  private current: Open | null = null;

  private readonly host: LightboxHost;

  constructor(host: LightboxHost) {
    this.host = host;
  }

  get opened(): boolean {
    return this.current !== null;
  }

  /** Индекс показанной карточки; null — закрыт. */
  get index(): number | null {
    return this.current?.index ?? null;
  }

  open(cards: GalleryCard[], index: number): void {
    this.close();
    const { host } = this;
    const box = host.el('div', 'lightbox');
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-label', host.t('gallery'));

    const image = document.createElement('img');
    image.className = 'lightbox-image';
    image.decoding = 'async';
    const caption = host.el('div', 'lightbox-caption');
    const counter = host.el('div', 'lightbox-counter');
    const stage = host.el('div', 'lightbox-stage');
    stage.append(image, caption);

    const close = this.button('lightbox-close', '✕', host.t('gallery_close'));
    close.addEventListener('click', () => this.close());
    const prev = this.button('lightbox-prev', '‹', host.t('gallery_prev'));
    prev.addEventListener('click', () => this.step(-1));
    const next = this.button('lightbox-next', '›', host.t('gallery_next'));
    next.addEventListener('click', () => this.step(1));
    box.addEventListener('click', (event) => {
      if (event.target === box || event.target === stage) this.close();
    });
    box.append(counter, close, prev, stage, next);

    this.current = { box, image, caption, counter, cards, index, opener: host.activeElement() };
    host.root.appendChild(box);
    this.show(index);
    document.addEventListener('keydown', this.onKey, true);
    this.bindSwipe(box);
    close.focus();
  }

  step(delta: number): void {
    if (!this.current) return;

    const { cards, index } = this.current;
    this.show((index + delta + cards.length) % cards.length);
  }

  close(): void {
    if (!this.current) return;

    document.removeEventListener('keydown', this.onKey, true);
    this.current.box.remove();
    this.current.opener?.focus?.();
    this.current = null;
  }

  private show(index: number): void {
    if (!this.current) return;

    const card = this.current.cards[index];
    if (!card) return;

    this.current.index = index;
    this.current.image.src = card.image;
    this.current.image.alt = card.title;
    const { price, rest } = splitPrice(card.meta);
    this.current.caption.replaceChildren(
      this.host.el('div', 'lightbox-title', card.title),
      ...(rest ? [this.host.el('div', 'lightbox-meta', rest)] : []),
      ...(price ? [this.host.el('div', 'lightbox-price', price)] : []),
    );
    this.current.counter.textContent = `${index + 1} / ${this.current.cards.length}`;
    this.current.box.classList.toggle('lightbox-single', this.current.cards.length < 2);
  }

  private button(cls: string, glyph: string, label: string): HTMLButtonElement {
    const button = this.host.el('button', cls, glyph);
    button.type = 'button';
    button.setAttribute('aria-label', label);
    button.title = label;
    return button;
  }

  private readonly onKey = (event: KeyboardEvent): void => {
    if (!this.current) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'ArrowLeft') {
      this.step(-1);
    } else if (event.key === 'ArrowRight') {
      this.step(1);
    }
  };

  private bindSwipe(box: HTMLDivElement): void {
    let startX = 0;
    box.addEventListener(
      'touchstart',
      (event) => {
        startX = event.changedTouches[0]?.clientX ?? 0;
      },
      { passive: true },
    );
    box.addEventListener(
      'touchend',
      (event) => {
        const delta = (event.changedTouches[0]?.clientX ?? 0) - startX;
        if (Math.abs(delta) > SWIPE_MIN_PX) this.step(delta < 0 ? 1 : -1);
      },
      { passive: true },
    );
  }
}
